/**
 * Admin Teams Page
 * Manage team rosters and captain assignments per game
 */

import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import type { PlayerRosterDocument } from '@schemas/firestore';

// Game definitions with display info
const GAMES = [
    { id: 'smashSolo', name: 'Smash Bros Solo', icon: '🎮', color: 'bg-red-500' },
    { id: 'smashCrews', name: 'Smash Bros Crews', icon: '👥', color: 'bg-red-600' },
    { id: 'smashJVCrews', name: 'Smash Bros JV Crews', icon: '🛡️', color: 'bg-amber-500' },
    { id: 'rocketLeague', name: 'Rocket League', icon: '🚗', color: 'bg-blue-500' },
    { id: 'marioKart', name: 'Mario Kart', icon: '🏎️', color: 'bg-green-500' },
    { id: 'madden', name: 'Madden NFL', icon: '🏈', color: 'bg-orange-500' },
    { id: 'fortnite', name: 'Fortnite', icon: '🎯', color: 'bg-purple-500' },
    { id: 'broadcasting', name: 'Broadcasting', icon: '📹', color: 'bg-gray-500' },
] as const;

type GameId = typeof GAMES[number]['id'];

interface PlayerWithRoster extends PlayerRosterDocument {
    id: string;
}

export default function AdminTeamsPage() {
    const [players, setPlayers] = useState<PlayerWithRoster[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedGame, setExpandedGame] = useState<GameId | null>(null);
    const [updating, setUpdating] = useState<string | null>(null);

    // Load all players
    useEffect(() => {
        async function loadPlayers() {
            try {
                const snap = await getDocs(collection(db, 'playerRoster'));
                const data = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                } as PlayerWithRoster));
                setPlayers(data.filter(p => p.status === 'active'));
            } catch (err) {
                console.error('Error loading players:', err);
            } finally {
                setLoading(false);
            }
        }
        loadPlayers();
    }, []);

    // Get players for a specific game
    const getPlayersForGame = (gameId: GameId) => {
        return players.filter(p => p.games?.[gameId as keyof typeof p.games]);
    };

    // Toggle captain status
    const toggleCaptain = async (playerId: string, gameId: GameId) => {
        setUpdating(playerId);
        try {
            const player = players.find(p => p.id === playerId);
            if (!player) return;

            const currentStatus = player.isCaptain?.[gameId as keyof typeof player.isCaptain] || false;
            const newIsCaptain = {
                ...player.isCaptain,
                [gameId]: !currentStatus
            };

            await updateDoc(doc(db, 'playerRoster', playerId), {
                isCaptain: newIsCaptain
            });

            // Update local state
            setPlayers(prev => prev.map(p =>
                p.id === playerId
                    ? { ...p, isCaptain: newIsCaptain }
                    : p
            ));
        } catch (err) {
            console.error('Error updating captain status:', err);
        } finally {
            setUpdating(null);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-5xl">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-display font-bold text-white">Team Manager</h1>
                <p className="text-secondary-400">View players by game and assign team captains</p>
            </div>

            {/* Games Grid */}
            <div className="space-y-4">
                {GAMES.map(game => {
                    const gamePlayers = getPlayersForGame(game.id);
                    const captainCount = gamePlayers.filter(p =>
                        p.isCaptain?.[game.id as keyof typeof p.isCaptain]
                    ).length;
                    const isExpanded = expandedGame === game.id;

                    return (
                        <div key={game.id} className="card">
                            {/* Game Header - Clickable */}
                            <button
                                onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                                className="w-full flex items-center justify-between p-4 -m-4 hover:bg-secondary-800/50 rounded-lg transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 ${game.color} rounded-lg flex items-center justify-center text-2xl`}>
                                        {game.icon}
                                    </div>
                                    <div className="text-left">
                                        <h2 className="text-lg font-semibold text-white">{game.name}</h2>
                                        <p className="text-sm text-secondary-400">
                                            {gamePlayers.length} player{gamePlayers.length !== 1 ? 's' : ''}
                                            {captainCount > 0 && (
                                                <span className="ml-2 text-yellow-400">
                                                    👑 {captainCount} captain{captainCount !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <svg
                                    className={`w-5 h-5 text-secondary-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Expanded Player List */}
                            {isExpanded && (
                                <div className="mt-4 pt-4 border-t border-secondary-700">
                                    {gamePlayers.length === 0 ? (
                                        <p className="text-secondary-500 text-sm py-4 text-center">
                                            No players enrolled in {game.name}
                                        </p>
                                    ) : (
                                        <div className="space-y-2">
                                            {gamePlayers.map(player => {
                                                const isCaptain = player.isCaptain?.[game.id as keyof typeof player.isCaptain] || false;
                                                const isUpdatingThis = updating === player.id;

                                                return (
                                                    <div
                                                        key={player.id}
                                                        className="flex items-center justify-between p-3 bg-secondary-800/50 rounded-lg"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-secondary-700 rounded-full flex items-center justify-center">
                                                                <span className="text-sm font-medium text-secondary-300">
                                                                    {player.firstName?.[0]}{player.lastName?.[0]}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-white">
                                                                    {player.firstName} {player.lastName}
                                                                    {isCaptain && <span className="ml-2">👑</span>}
                                                                </p>
                                                                <p className="text-sm text-secondary-400">
                                                                    Grade {player.grade}
                                                                    {player.gamerTag && (
                                                                        <span className="ml-2 text-primary-400">@{player.gamerTag}</span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Captain Toggle */}
                                                        <button
                                                            onClick={() => toggleCaptain(player.id, game.id)}
                                                            disabled={isUpdatingThis}
                                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isCaptain
                                                                ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                                                                : 'bg-secondary-700 text-secondary-300 hover:bg-secondary-600'
                                                                } ${isUpdatingThis ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            {isUpdatingThis ? (
                                                                <span className="flex items-center gap-1">
                                                                    <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                                                </span>
                                                            ) : isCaptain ? (
                                                                '👑 Captain'
                                                            ) : (
                                                                'Make Captain'
                                                            )}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Quick Stats */}
            <div className="mt-8 p-4 bg-secondary-800/50 rounded-lg border border-secondary-700">
                <h3 className="text-sm font-semibold text-secondary-400 uppercase tracking-wider mb-3">Quick Stats</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-2xl font-bold text-white">{players.length}</p>
                        <p className="text-sm text-secondary-400">Active Players</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-white">
                            {GAMES.filter(g => getPlayersForGame(g.id).length > 0).length}
                        </p>
                        <p className="text-sm text-secondary-400">Active Games</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-yellow-400">
                            {players.filter(p => Object.values(p.isCaptain || {}).some(v => v)).length}
                        </p>
                        <p className="text-sm text-secondary-400">Team Captains</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-primary-400">
                            {players.filter(p => p.discordId).length}
                        </p>
                        <p className="text-sm text-secondary-400">Discord Linked</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
